import type { ComponentProps, ReactNode } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { fromItemValue, rootValue, toItemValue, type SelectOption } from './optionSelectModel';

export type { SelectOption } from './optionSelectModel';

type TriggerProps = Omit<ComponentProps<typeof SelectTrigger>, 'value' | 'defaultValue' | 'onChange' | 'children' | 'dir'>;

interface OptionSelectProps extends TriggerProps {
  value: string;
  onValueChange: (value: string) => void;
  options: readonly SelectOption[];
  /** Shown while `value` is '' and no option carries '' ("Pick", "Loading…"). */
  placeholder?: ReactNode;
  disabled?: boolean;
  /** Classes for the open list (e.g. `font-mono` for slugs). */
  contentClassName?: string;
}

/**
 * The registry Select for a flat list of choices. Every other prop (`id`, `data-testid`, `aria-*` from fieldA11y,
 * `className`, `size`) lands on the trigger, so labels and test ids keep working. Each item carries `data-value` with
 * the caller's value, which the E2E scripts click.
 */
export function OptionSelect({ value, onValueChange, options, placeholder, disabled, contentClassName, ...trigger }: OptionSelectProps) {
  const onChange = (v: string) => {
    const next = fromItemValue(v);
    if (next !== undefined) onValueChange(next);
  };
  return (
    <Select value={rootValue(value, options)} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger {...trigger}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className={contentClassName}>
        {options.map((o) => (
          <SelectItem key={o.value} value={toItemValue(o.value)} disabled={o.disabled} data-value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
